import React, { ComponentType, ReactNode, SFC } from 'react';

interface IRenderProps<T> {
  children: (value: T) => ReactNode;
}

/**
 * This function works similarly to `connect` from `react-redux`. This creates a function
 * that takes a component, and injects the value of the provided `RenderPropComponent` and
 * into the prop named `propName`. The `RenderPropComponent` will not be passed any additional
 * props. See the corresponding test for example usage.
 *
 * Because of how types work, this will not correctly check if all parameters the component
 * takes are optional.
 *
 * @deprecated Please use hooks instead
 */
export function connectRenderProp<K extends string, V>(
  propName: K,
  RenderPropComponent: ComponentType<IRenderProps<V>>,
) {
  return <ComponentProps extends { [J in K]?: V }>(Wrap: ComponentType<ComponentProps>) => {
    const Connected: SFC<Omit<ComponentProps, K>> = (props) => (
      <RenderPropComponent>{(value: V) => {
        const injectedProps = { [propName]: value };
        // TYPESCRIPT: Work around typescript limitation with generics. See
        // https://github.com/Microsoft/TypeScript/issues/28938
        return <Wrap {...props as ComponentProps} {...injectedProps} />;
      }}
      </RenderPropComponent>
    );
    Connected.displayName = `ConnectRP(${Wrap.displayName})`;

    return Connected;
  };
}
